import { isDefinedString, reduce } from "et3-type-guards";
import { memoize } from "lodash";
import { computed, observable, runInAction } from "mobx";

import { apiCallChangePassword } from "../../../api/core-api/api-call-change-password";
import { apiCallGetMyPermission } from "../../../api/core-api/api-call-get-my-permissions";
import apiCallInsertEvent from "../../../api/core-api/api-call-insert-event";
import { isValidPassword } from "../../../helpers/et3-data-helper";
import { E_ADMIN_ROLE, TAdmin, TAdminRole, TCorePage } from "../../../Types";

import getCorePopUpStore from "../core-popup-store";
import configuration from "../../../configuration/keys";
import {
  E_LOCAL_STORAGE_KEY,
  E_STORAGE_TYPE
} from "../../../constants/storage-constants";
import getCoreStorageStore from "../core-storage-store";
import { apiCallGetAdminVersionsStates } from "../../../api/core-api/api-get-admin-version";

class CorePersonalStore {
  //default id is 0 before loading finish, onSignout/not Siged in it's -1 (to handle cases)
  adminCurrentVersionIsActive = observable.box<boolean>(true);
  adminUnderMaintenance = observable.box<boolean>(false);
  adminNewUpdateVersion = observable.box<string>(configuration.version);
  user = observable.box<TAdmin>({
    id: 0,
    username: "",
    fullName: "",
    role: E_ADMIN_ROLE.NO_ROLE,
    lastLogin: "",
    needResetPassword: false
  });
  isUserAuthinticated = observable.box<boolean>(false);
  oldPassword = observable.box<string>("");
  newPassword = observable.box<string>("");
  confirmPassword = observable.box<string>("");
  userPermissions = observable.box<number[]>([]);
  userPofilePic = observable.box<string>("avatar");

  setAdminVersionStates = ({
    isActive,
    isUnderMaintenance,
    latestAdminVersion
  }: {
    isActive: boolean;
    isUnderMaintenance: boolean;
    latestAdminVersion: string;
  }) => {
    runInAction(() => {
      this.adminCurrentVersionIsActive.set(isActive);
      this.adminUnderMaintenance.set(isUnderMaintenance);
      this.adminNewUpdateVersion.set(latestAdminVersion);
    });
  };

  setOldPassword = (value: string) => {
    runInAction(() => {
      this.oldPassword.set(value);
    });
  };

  setNewPassword = (value: string) => {
    runInAction(() => {
      this.newPassword.set(value);
    });
  };

  setConfirmPassword = (value: string) => {
    runInAction(() => {
      this.confirmPassword.set(value);
    });
  };

  getIsChangePasswordEnabled = () =>
    isDefinedString(this.oldPassword.get()) &&
    isDefinedString(this.newPassword.get()) &&
    isDefinedString(this.confirmPassword.get());

  changePasswordHandler = async () => {
    const oldPassword = this.oldPassword.get().trim();
    const newPassword = this.newPassword.get().trim();
    const confirmPassword = this.confirmPassword.get().trim();

    if (newPassword !== confirmPassword) {
      getCorePopUpStore().showAlertMessage("INCORRECT_NEWPASSWORD");
      return;
    }

    if (!isValidPassword(newPassword)) {
      getCorePopUpStore().showAlertMessage("INVALID_PASSWORD");
      return;
    }
    if (oldPassword === newPassword) {
      getCorePopUpStore().showAlertMessage("INCORRECT_PASSWORD_REPEATED");
      return;
    }

    const { status } = await apiCallChangePassword({
      oldPassword,
      newPassword
    });
    if (status === "OK") {
      getCorePopUpStore().showSuccessMessage("PASSWORD_CHANGE_SUCCESSFUL");
      getCorePopUpStore().closeChangePasswordPopUps();
      const userPersonalData = getCorePersonalStore().user.get();
      userPersonalData.needResetPassword = false;
      getCorePersonalStore().setUser(userPersonalData);
      this.resetInputFields();
    } else if (status === "ERROR") {
      getCorePopUpStore().showAlertMessage("INCORRECT_OLD_PASSWORD");
    }
  };
  setUser = (user: TAdmin) => {
    this.user.set(user);
    const userWithAdminFlag = {
      ...user
    };
    getCoreStorageStore(E_STORAGE_TYPE.LOCAL).set(
      E_LOCAL_STORAGE_KEY.USER,
      userWithAdminFlag
    );
  };
  setUserPermission = (
    permission: {
      moduleId: number;
      description: string;
      hasPermission: 0 | 1;
    }[]
  ) => {
    const activePermissions = permission
      .filter((item) => item.hasPermission === 1)
      .map((item) => item.moduleId);
    this.userPermissions.set(activePermissions);
    getCoreStorageStore(E_STORAGE_TYPE.LOCAL).set(
      E_LOCAL_STORAGE_KEY.USER_PERMISSIONS,
      activePermissions
    );
  };
  getMyPermission = async () => {
    const { data, status } = await apiCallGetMyPermission();
    if (status === "OK") {
      getCorePersonalStore().setUserPermission(data);
    }
  };
  insertAdminEvent = async ({
    eventName,
    fromPage,
    userID,
    extraData
  }: {
    eventName: string;
    fromPage: TCorePage;
    userID: number;
    extraData: unknown;
  }) => {
    return await apiCallInsertEvent({
      eventName,
      fromPage,
      userID,
      extraData
    });
  };
  setUserProfilePic = () => {
    const userRole = reduce(() => this.user.get().role, "");

    switch (userRole) {
      case E_ADMIN_ROLE.BOT:
        this.userPofilePic.set("avatar");
        break;

      case E_ADMIN_ROLE.CEO:
        this.userPofilePic.set("ceo");
        break;

      case E_ADMIN_ROLE.CTO:
        this.userPofilePic.set("cto");
        break;

      case E_ADMIN_ROLE.KHATTABA_ADMIN:
      case E_ADMIN_ROLE.KHATTABA_ADMIN_AI:
      case E_ADMIN_ROLE.KHATTABA_ADMIN_TESTER:
        this.userPofilePic.set("alkhattaba");
        break;

      default:
        this.userPofilePic.set("customers-support");
        break;
    }
  };

  resetInputFields = () => {
    this.oldPassword.set("");
    this.newPassword.set("");
    this.confirmPassword.set("");
  };

  getAdminVersionStatesFromDatabase = async () => {
    const { status, data, code } = await apiCallGetAdminVersionsStates({
      version: configuration.version
    });
    if (status === "OK") {
      this.setAdminVersionStates(data);
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  adminServiceIsActive = () => {
    return (
      this.adminCurrentVersionIsActive.get() &&
      !this.adminUnderMaintenance.get()
    );
  };

  getStoredRole = (): TAdminRole => {
    const userFromStorage = getCoreStorageStore(
      E_STORAGE_TYPE.LOCAL
    ).get<TAdmin>(E_LOCAL_STORAGE_KEY.USER);
    return reduce(() => userFromStorage.role, E_ADMIN_ROLE.NO_ROLE);
  };

  isManualKhattabaAdmin = computed(() => {
    const currentRole = <TAdminRole>this.user.get().role;
    const storedRole = this.getStoredRole();

    if (!isDefinedString(currentRole) || !isDefinedString(storedRole)) {
      return false;
    }

    const manualKhattabaRoles: TAdminRole[] = [
      E_ADMIN_ROLE.KHATTABA_ADMIN,
      E_ADMIN_ROLE.KHATTABA_ADMIN_TESTER
    ];

    return [currentRole, storedRole].some((role) =>
      manualKhattabaRoles.includes(role)
    );
  });

  isKhattabaAiAdmin = computed(() => {
    const currentRole = <TAdminRole>this.user.get().role;
    const storedRole = this.getStoredRole();

    if (!isDefinedString(currentRole) || !isDefinedString(storedRole)) {
      return false;
    }

    const khattabaAiRoles: TAdminRole[] = [E_ADMIN_ROLE.KHATTABA_ADMIN_AI];

    return (
      khattabaAiRoles.includes(currentRole) ||
      khattabaAiRoles.includes(storedRole)
    );
  });

  isAnyKhattabaAdmin = computed(() => {
    return this.isManualKhattabaAdmin.get() || this.isKhattabaAiAdmin.get();
  });
}

const getCorePersonalStore = memoize(
  () => new CorePersonalStore(),
  () => 1
);
export default getCorePersonalStore;
